import type { ShortcutBindingConflict, ShortcutSettingRow } from "~/keyboard/shortcutSettingsModel";

import { Alert, Box, Button, Chip, Icon, IconButton, Tooltip } from "@mui/material";
import { Fragment } from "react";

import { ClearButton } from "~/components/Buttons";
import Keycap from "~/components/keyboard/Keycap";
import { CaptionText } from "~/components/Typographies";
import ShortcutRecorderField from "~settings/shortcuts/components/ShortcutRecorderField";

interface ShortcutRowControlProps {
	conflicts: ShortcutBindingConflict[];
	isMac: boolean;
	isRecording: boolean;
	onClearConflicts: () => void;
	onCommit: (binding: string) => void;
	onDisable: () => void;
	onRecord: () => void;
	onReset: () => void;
	onStopRecording: () => void;
	onSwap: () => void;
	row: ShortcutSettingRow;
}

const sourceChipColor = (source: ShortcutSettingRow["source"]) => (source === "custom" ? "primary" : "default");

const sourceChipLabel = (source: ShortcutSettingRow["source"]) =>
	source === "custom" ? "Custom" : source === "disabled" ? "Disabled" : "Default";

const ShortcutRowControl = ({
	conflicts,
	isMac,
	isRecording,
	onClearConflicts,
	onCommit,
	onDisable,
	onRecord,
	onReset,
	onStopRecording,
	onSwap,
	row
}: ShortcutRowControlProps) => {
	if (isRecording) {
		return <ShortcutRecorderField onCancel={onStopRecording} onCommit={onCommit} />;
	}

	return (
		<Box sx={{ alignItems: "flex-end", display: "flex", flexDirection: "column", gap: 0.75 }}>
			<Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
				<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
					{row.bindings.length === 0 ? (
						<CaptionText color="text.disabled">No binding</CaptionText>
					) : (
						row.bindings.map((binding, index) => (
							<Fragment key={binding}>
								{index > 0 && <CaptionText color="text.disabled">or</CaptionText>}
								<Keycap binding={binding} isMac={isMac} />
							</Fragment>
						))
					)}
				</Box>
				<Chip color={sourceChipColor(row.source)} label={sourceChipLabel(row.source)} size="small" variant="outlined" />
				<Tooltip title="Record a new shortcut">
					<IconButton aria-label={`Record shortcut for ${row.label}`} onClick={onRecord} size="small">
						<Icon fontSize="small">edit</Icon>
					</IconButton>
				</Tooltip>
				<Tooltip title="Reset to default">
					<span>
						<IconButton
							aria-label={`Reset ${row.label} to default`}
							disabled={row.source === "default"}
							onClick={onReset}
							size="small"
						>
							<Icon fontSize="small">restart_alt</Icon>
						</IconButton>
					</span>
				</Tooltip>
				<Tooltip title="Disable this shortcut">
					<span>
						<IconButton
							aria-label={`Disable ${row.label}`}
							disabled={row.source === "disabled"}
							onClick={onDisable}
							size="small"
						>
							<Icon fontSize="small">block</Icon>
						</IconButton>
					</span>
				</Tooltip>
			</Box>
			{conflicts.length > 0 && (
				<Alert
					action={
						<Box sx={{ display: "flex", gap: 0.5 }}>
							<ClearButton color="inherit" onClick={onClearConflicts} size="small">
								Clear other
							</ClearButton>
							<Button color="inherit" onClick={onSwap} size="small">
								Swap
							</Button>
						</Box>
					}
					severity="warning"
					sx={{ maxWidth: 360 }}
				>
					Also used by {conflicts.map((conflict) => conflict.label).join(", ")}.
				</Alert>
			)}
		</Box>
	);
};

export default ShortcutRowControl;
